import { DateTimeFormatOptions } from "next-intl"

import { CustomerData } from "@/lib/intershop/interfaces/customer/customer"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { getCountry, getCurrency } from "@/lib/locale/locale-common-utils"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"

import { logger } from "./logger"

export async function loadInParallel<T1, T2>(f1: Promise<T1>, f2: Promise<T2>): Promise<[T1, T2]> {
  const promises = [f1, f2]

  const parallelResult = await Promise.allSettled(promises)
  let result1: any
  if (parallelResult[0].status === "fulfilled") {
    result1 = parallelResult[0].value
  }
  let result2: any
  if (parallelResult[1].status === "fulfilled") {
    result2 = parallelResult[1].value
  }
  return [result1, result2]
}

// throws an exception if any of the promises fails
export async function loadInParallel5<T1, T2, T3, T4, T5>(
  f1: Promise<T1>,
  f2: Promise<T2>,
  f3: Promise<T3>,
  f4: Promise<T4>,
  f5: Promise<T5>
): Promise<[T1, T2, T3, T4, T5]> {
  const promises = [f1, f2, f3, f4, f5]

  const results = await Promise.allSettled(promises)

  return results.map((res, index) => {
    if (res.status === "fulfilled") {
      return res.value
    } else {
      logger.warn(`Promise ${index} failed with reason ${res.reason}`)
      throw res.reason
    }
  }) as [T1, T2, T3, T4, T5]
}

export function formatPrice(isoLocale: string, price?: number, currency?: string): string {
  if (price !== undefined && currency) {
    return new Intl.NumberFormat(isoLocale, {
      style: "currency",
      currency: currency,
    }).format(price)
  }
  return ""
}

export function formatDate(isoLocale: string, dateOrString?: Date | string, options?: DateTimeFormatOptions): string {
  if (dateOrString !== undefined) {
    const date = typeof dateOrString === "string" ? new Date(dateOrString) : dateOrString
    return new Intl.DateTimeFormat(isoLocale, options ?? {}).format(date)
  }
  return ""
}

export function formatDateLegacy(date?: Date): string {
  if (date !== undefined) {
    return new Intl.DateTimeFormat("de-DE", { dateStyle: "short" }).format(date)
  }
  return ""
}

export function formatAndReplacePrices(isoLocale: string, stringWithUnformattedPrices: string): string {
  if (!stringWithUnformattedPrices) {
    return ""
  }

  const currency = getCurrency(isoLocale)
  const formatted = stringWithUnformattedPrices.replace(/\b\d+\.\d{2}\b/g, (match) => {
    const price = parseFloat(match)
    return formatPrice(isoLocale, price, currency)
  })

  return formatted
}

export function getLastElement(subject: string, separator: string): string {
  return subject.split(separator).slice(-1)[0]
}

export function showContentCountryDependent(isoLocale: string, countriesToExclude: any) {
  const currentCountry = getCountry(isoLocale)
  const currentCountryExcluded: boolean = countriesToExclude?.includes(currentCountry) || false
  return !currentCountryExcluded
}

export function truncateString(str: string, length: number = 100): string {
  return str.length > length ? str.slice(0, length) + "..." : str
}

export async function readStreamBody(stream: ReadableStream) {
  const reader = stream.getReader()
  const chunks: Uint8Array[] = []
  let result

  while (!(result = await reader.read()).done) {
    chunks.push(result.value)
  }

  return new TextDecoder().decode(Buffer.concat(chunks))
}

export function getString(str: any): string {
  if (typeof str == "string") {
    return str as string
  }
  return ""
}

function getNumber(str: any): number {
  if (typeof str == "number") {
    return str as number
  }
  return -9.99
}

export function getBoolean(str: any): boolean {
  if (typeof str == "boolean") {
    return str as boolean
  }
  if (typeof str == "string") {
    return str.toLowerCase() == "true"
  }
  return false
}

export function round(number: number) {
  return Math.round((number + Number.EPSILON) * 100) / 100
}

export function hasEntries(record: Record<any, any>) {
  return Object.entries(record).length > 0
}

export function useHasHover() {
  try {
    return matchMedia("(hover: hover)").matches
  } catch {
    // Assume that if browser too old to support matchMedia it's likely not a touch device
    return true
  }
}

export const countries = ["germany", "austria", "switzerland", "france", "netherlands"]
export const countryForCountryCodeMap = {
  DE: "germany",
  AT: "austria",
  CH: "switzerland",
  FR: "france",
  NL: "netherlands",
}

export function getEmptyAddress(type: string) {
  return {
    id: "",
    name: "",
    type: "",
    urn: "",
    externalID: "",
    externalUrn: "",
    addressName: "",
    taxationID: "",
    title: "",
    aristocraticTitle: "",
    jobTitle: "",
    honorific: "",
    firstName: "",
    secondName: "",
    lastName: "",
    secondLastName: "",
    companyName1: "",
    companyName2: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    postBox: "",
    mainDivision: "",
    mainDivisionName: "",
    subDivision: "",
    subDivisionName: "",
    postalCode: "",
    email: "",
    phoneMobile: "",
    phoneHome: "",
    phoneBusiness: "",
    phoneBusinessDirect: "",
    country: "",
    countryCode: "",
    city: "",
    fax: "",
    shipFromAddress: false,
    serviceToAddress: false,
    installToAddress: false,
    invoiceToAddress: type === "invoiceTo",
    shipToAddress: type === "shipTo",
    preferredShippingAddress: "false",
    preferredBillingAddress: "false",
  } as ExtendedAddressRO
}

export function getInitialAddress(
  userData: UserData | undefined,
  customerData: CustomerData | undefined,
  countryCodeUppercase: string,
  countryName
) {
  return {
    id: "",
    name: "",
    type: "",
    urn: "",
    externalID: "",
    externalUrn: "",
    addressName: "",
    taxationID: "",
    title: userData?.title || "",
    aristocraticTitle: "",
    jobTitle: "",
    honorific: "",
    firstName: userData?.firstName ?? "",
    secondName: "",
    lastName: userData?.lastName ?? "",
    secondLastName: "",
    companyName1: customerData?.companyName ?? "",
    companyName2: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    postBox: "",
    mainDivision: "",
    mainDivisionName: "",
    subDivision: "",
    subDivisionName: "",
    postalCode: "",
    email: userData?.email ?? "",
    phoneMobile: "",
    phoneHome: "",
    phoneBusiness: "",
    phoneBusinessDirect: "",
    country: countryName,
    countryCode: countryCodeUppercase,
    city: "",
    fax: "",
    shipFromAddress: false,
    serviceToAddress: false,
    installToAddress: false,
    invoiceToAddress: true,
    shipToAddress: true,
    preferredShippingAddress: "true",
    preferredBillingAddress: "true",
  } as ExtendedAddressRO
}
